/*
================================================================================
FILE HEADER — apps/studio-web/src/lib/ollama.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed         |
|----|------------|-------------------|--------------------------|----------------------|
| 1  | 2026-07-02 | Claude Sonnet 4.6 | Tier-2 Ollama (Phase 3)  | Created — Ollama     |
|    |            |                   |                          | client for local VLM |
|    |            |                   |                          | draftSteps +         |
|    |            |                   |                          | triageFailure        |
| 2  | 2026-07-02 | Claude Sonnet 5   | Fix model name + bugs    | model name "qwen2.5- |
|    |            |                   |                          | vl"→"qwen2.5vl"      |
|    |            |                   |                          | (Ollama library has  |
|    |            |                   |                          | no hyphen before vl);|
|    |            |                   |                          | fixed hasVisionModel |
|    |            |                   |                          | detection (never     |
|    |            |                   |                          | matched correct name);|
|    |            |                   |                          | added catch around   |
|    |            |                   |                          | streaming read loop  |
|    |            |                   |                          | (raw AbortError was  |
|    |            |                   |                          | leaking to UI);       |
|    |            |                   |                          | default timeout       |
|    |            |                   |                          | 60s→120s for 7B on   |
|    |            |                   |                          | CPU-only hosts        |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/App.tsx
- apps/studio-web/src/components/SettingsPage.tsx
- apps/studio-web/src/lib/__tests__/ollama.test.ts

FUNCTIONS / EXPORTS COVERED
----------------------------
- OllamaConfig         — endpoint + model + timeout
- OllamaCheckResult    — result of checkOllamaReachable()
- checkOllamaReachable — ping /api/tags, return available models
- triageFailure        — explain why a step failed (streaming)
- draftSteps           — suggest next test steps from current page (streaming)
- serializeARIAForLLM  — converts SerializedAXTree into compact LLM-readable text

CALLER → CALLEE MAP
--------------------
- App.tsx handleRequestTriage()    → triageFailure()
- App.tsx handleRequestDraftSteps() → draftSteps()
- SettingsPage onTestConnection()  → checkOllamaReachable()

IMPORTANT POINTS
----------------
1. All calls are plain fetch() to http://localhost:11434 (or configured endpoint).
   The Studio SPA is served from a different origin, so Ollama must allow CORS.
   Ollama ≥ 0.1.24 accepts CORS from all origins by default when the
   OLLAMA_ORIGINS env var includes the Studio origin (e.g. "*" or "http://localhost:5173").
   Users must set: OLLAMA_ORIGINS=* ollama serve (or add to system service).

2. Images passed to Ollama MUST be raw base64 — WITHOUT the
   "data:image/png;base64," prefix. This function strips the prefix automatically.

3. Streaming: /api/chat with stream:true returns newline-delimited JSON.
   Each line: {"message":{"content":"partial text"},"done":false}
   Final line: {"done":true}
   onChunk() is called for each partial; the full accumulated text is returned.

4. Timeout: default 60s. The 7B model can be slow on CPU-only machines.
   The 3B model is faster (~15–25 tok/s on a modern laptop).

5. triageFailure sends: screenshot + ARIA tree text + failing step description.
   draftSteps sends: screenshot + ARIA tree text + page URL/title.
   Neither function auto-acts on the browser — output is suggestion-only (Tier-2 contract).

ISSUES FACED / KNOWN GOTCHAS
-----------------------------
- CORS: The most common failure mode. If fetch throws a TypeError (network error)
  and Ollama is actually running, it's almost certainly a CORS block.
  Error message shown to user: "CORS error — start Ollama with OLLAMA_ORIGINS=*"
- The qwen2.5vl model must be pulled before use: `ollama pull qwen2.5vl:7b`
  (Ollama's library name has NO hyphen before "vl" — "qwen2.5-vl" 404s on pull.)
  checkOllamaReachable detects this and reports hasVisionModel correctly.
================================================================================
*/

import type { SerializedAXTree, SerializedAXNode } from '../extension/protocol.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OllamaConfig {
  endpoint:  string;   // e.g. "http://localhost:11434"
  model:     string;   // e.g. "qwen2.5vl:7b" (Ollama library name — no hyphen before "vl")
  timeoutMs?: number;  // default 60 000
}

export interface OllamaCheckResult {
  ok:              boolean;
  hasVisionModel:  boolean;
  availableModels: string[];
  error?:          string;
}

// ─── ARIA tree → text ────────────────────────────────────────────────────────

/** Flatten the ARIA tree to a compact, LLM-readable text block.
 *  Focuses on interactive nodes — buttons, inputs, links, and text labels.
 *  Truncates at ~4 000 chars to stay within reasonable prompt budget.
 */
export function serializeARIAForLLM(tree: SerializedAXTree): string {
  const lines: string[] = [
    `PAGE: ${tree.url}`,
    `TITLE: ${tree.title}`,
    `VIEWPORT: ${tree.viewportWidth}×${tree.viewportHeight}`,
    '',
    'INTERACTIVE ELEMENTS:',
  ];

  const INTERACTIVE_ROLES = new Set([
    'button', 'link', 'textbox', 'combobox', 'listbox', 'menuitem',
    'checkbox', 'radio', 'switch', 'tab', 'option', 'menuitemcheckbox',
    'menuitemradio', 'searchbox', 'spinbutton', 'slider',
  ]);
  const CONTENT_ROLES = new Set(['heading', 'img', 'text', 'label', 'paragraph']);

  let count = 0;

  function walk(node: SerializedAXNode & { children?: SerializedAXNode[] }, depth: number): void {
    if (count >= 120) return; // hard cap to avoid huge prompts
    const indent = '  '.repeat(Math.min(depth, 4));
    const isInteractive = INTERACTIVE_ROLES.has(node.role);
    const isContent     = CONTENT_ROLES.has(node.role);

    if (isInteractive || isContent) {
      const parts: string[] = [`${indent}[${node.role}]`];
      if (node.name)        parts.push(`"${node.name}"`);
      if (node.placeholder) parts.push(`placeholder="${node.placeholder}"`);
      if (node.value)       parts.push(`value="${String(node.value).slice(0, 60)}"`);
      if (node.checked !== undefined) parts.push(`checked=${node.checked}`);
      if (node.disabled)    parts.push('disabled');
      if (node.attributes?.['data-testid']) parts.push(`testid="${node.attributes['data-testid']}"`);
      if (node.bounds) {
        const b = node.bounds;
        parts.push(`@(${Math.round(b.x)},${Math.round(b.y)})`);
      }
      lines.push(parts.join(' '));
      count++;
    }

    node.children?.forEach(child => walk(child as SerializedAXNode & { children?: SerializedAXNode[] }, depth + 1));
  }

  walk(tree.root as SerializedAXNode & { children?: SerializedAXNode[] }, 0);

  if (count === 0) lines.push('  (no labeled interactive elements found)');

  const result = lines.join('\n');
  return result.length > 4000 ? result.slice(0, 4000) + '\n…(truncated)' : result;
}

// ─── Strip data URL prefix ───────────────────────────────────────────────────

function stripDataUrlPrefix(dataUrl: string): string {
  const idx = dataUrl.indexOf(',');
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
}

// ─── Ollama streaming fetch ───────────────────────────────────────────────────

interface OllamaChatMessage {
  role:    'user' | 'assistant' | 'system';
  content: string;
  images?: string[];
}

async function ollamaChat(
  config: OllamaConfig,
  messages: OllamaChatMessage[],
  onChunk?: (chunk: string) => void,
): Promise<string> {
  const controller = new AbortController();
  const timeoutMs   = config.timeoutMs ?? 120_000; // 7B vision models are slow on CPU-only hosts
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);

  const timeoutMessage = () =>
    `Ollama did not respond within ${Math.round(timeoutMs / 1000)}s. Vision models are slow on ` +
    `CPU-only hardware — try the smaller "qwen2.5vl:3b" model, or increase the timeout in Settings.`;

  let response: Response;
  try {
    response = await fetch(`${config.endpoint}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: config.model, messages, stream: true }),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    if (timedOut) throw new Error(timeoutMessage());
    const msg = err instanceof Error ? err.message : String(err);
    // Distinguish network-level failure (CORS / refused) from other errors
    if (msg.includes('fetch') || msg.includes('Failed') || msg.includes('NetworkError')) {
      throw new Error(`Cannot reach Ollama at ${config.endpoint}. Is it running? (CORS tip: start with OLLAMA_ORIGINS=*)`);
    }
    throw new Error(msg);
  }

  if (!response.ok) {
    clearTimeout(timer);
    const text = await response.text().catch(() => '');
    throw new Error(`Ollama returned HTTP ${response.status}: ${text.slice(0, 200)}`);
  }

  // Read streaming newline-delimited JSON
  const reader = response.body?.getReader();
  if (!reader) { clearTimeout(timer); throw new Error('No response body from Ollama'); }

  const decoder = new TextDecoder();
  let accumulated = '';
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const parsed = JSON.parse(trimmed) as {
            message?: { content: string };
            done: boolean;
          };
          if (parsed.message?.content) {
            accumulated += parsed.message.content;
            onChunk?.(parsed.message.content);
          }
          if (parsed.done) break;
        } catch { /* skip malformed line */ }
      }
    }
  } catch (err) {
    if (timedOut) throw new Error(timeoutMessage());
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Ollama stream interrupted: ${msg}`);
  } finally {
    clearTimeout(timer);
    reader.releaseLock();
  }

  return accumulated;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Ping Ollama to check reachability and list available models.
 * Returns whether any qwen2.5vl variant is available.
 */
export async function checkOllamaReachable(config: OllamaConfig): Promise<OllamaCheckResult> {
  try {
    const res = await fetch(`${config.endpoint}/api/tags`, {
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) {
      return { ok: false, hasVisionModel: false, availableModels: [], error: `HTTP ${res.status}` };
    }
    const data = await res.json() as { models?: Array<{ name: string }> };
    const models = (data.models ?? []).map(m => m.name);
    const hasVisionModel = models.some(m => m.includes('qwen2.5vl') || m.includes('qwen2-vl') || m.includes('llava'));
    return { ok: true, hasVisionModel, availableModels: models };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return {
      ok: false, hasVisionModel: false, availableModels: [],
      error: msg.includes('fetch') || msg.includes('Failed') || msg.includes('abort')
        ? `Ollama not reachable at ${config.endpoint}`
        : msg,
    };
  }
}

/**
 * Tier-2: explain why a step failed, in plain English.
 * Sends screenshot + ARIA tree + failed step description to the local VLM.
 * Returns the explanation text; calls onChunk with partial text while streaming.
 */
export async function triageFailure(
  config: OllamaConfig,
  opts: {
    screenshotDataUrl: string;
    ariaTree:          SerializedAXTree | null;
    failedStepType:    string;
    failedStepLabel:   string;
    failedStepSelector?: string;
    onChunk?: (chunk: string) => void;
  },
): Promise<string> {
  const ariaText = opts.ariaTree ? serializeARIAForLLM(opts.ariaTree) : '(ARIA tree not available)';
  const selectorHint = opts.failedStepSelector
    ? `\nPrimary selector tried: ${opts.failedStepSelector}`
    : '';

  const prompt = `You are a web test automation expert. A test step failed and I need a brief, practical explanation.

FAILED STEP
  Type:   ${opts.failedStepType}
  Target: ${opts.failedStepLabel}${selectorHint}

ARIA ACCESSIBILITY TREE (current page state):
${ariaText}

The screenshot shows the page state at the moment of failure.

Please respond in this exact format:
**Why it failed:** (1-2 sentences — what changed or is missing on the page)
**Evidence:** (what in the screenshot or ARIA tree supports this)
**Suggested fix:** (concrete action — e.g. update selector, add a wait, handle new modal)

Keep your total response under 150 words. Be direct and specific.`;

  return ollamaChat(
    config,
    [{ role: 'user', content: prompt, images: [stripDataUrlPrefix(opts.screenshotDataUrl)] }],
    opts.onChunk,
  );
}

/**
 * Tier-2: suggest next test steps from the current page state.
 * Sends a screenshot + ARIA tree and asks the VLM to draft 3-5 test steps.
 * Returns the suggestions text; calls onChunk with partial text while streaming.
 */
export async function draftSteps(
  config: OllamaConfig,
  opts: {
    screenshotDataUrl: string;
    ariaTree:          SerializedAXTree | null;
    pageTitle?:        string;
    pageUrl?:          string;
    onChunk?: (chunk: string) => void;
  },
): Promise<string> {
  const ariaText = opts.ariaTree ? serializeARIAForLLM(opts.ariaTree) : '(ARIA tree not available)';
  const pageCtx  = opts.pageTitle ? `Page: "${opts.pageTitle}" (${opts.pageUrl ?? ''})` : '';

  const prompt = `You are a web test automation expert helping a tester record meaningful test steps.
${pageCtx}

ARIA ACCESSIBILITY TREE (what's on this page right now):
${ariaText}

The screenshot shows the current browser state.

Suggest 3–5 meaningful test steps a tester should record next. Focus on verifying key functionality and user flows. For each step use this format:

1. [step type] "target element" — what it verifies or does
   Selector hint: (role + name or data-testid if visible)

Step types available: tap, typeText, assert (check text/visibility), wait, pressKey, screenshot.

Be concrete. Use element names from the ARIA tree. Keep the total response under 200 words.`;

  return ollamaChat(
    config,
    [{ role: 'user', content: prompt, images: [stripDataUrlPrefix(opts.screenshotDataUrl)] }],
    opts.onChunk,
  );
}
